import { useMemo, useState } from 'react'
import { Popover } from 'antd'
import { Icon } from '@iconify/react'

import { getCachedProjectEpicOptions } from './epicOptionsCache'
import EpicPickerPanel from './EpicPickerPanel'

import styles from './epicSelector.module.css'

export { mapEpicListToOptions, getEpicDisplayOptionFromTask } from './epicUtils'
export { clearProjectEpicOptionsCache } from './epicOptionsCache'
export { default as EpicPickerPanel } from './EpicPickerPanel'

const EpicSelector = ({
    projectId,
    value,
    onChange,
    options: controlledOptions,
    displayOption,
    placeholder = 'Select epic',
    disabled = false,
    enabled = true,
    compact = false,
    className,
}) => {
    const [visible, setVisible] = useState(false)

    const isControlled = Array.isArray(controlledOptions)

    const selectedItem = useMemo(() => {
        if (value == null || value === '') return null
        if (
            displayOption &&
            displayOption.value != null &&
            String(displayOption.value) === String(value)
        ) {
            return displayOption
        }
        if (isControlled) {
            const fromControlled =
                controlledOptions.find(
                    (item) => String(item.value) === String(value)
                ) || null
            if (fromControlled) return fromControlled
        }
        const cached = getCachedProjectEpicOptions(projectId)
        if (cached?.length) {
            return (
                cached.find((item) => String(item.value) === String(value)) ||
                null
            )
        }
        return null
    }, [value, displayOption, projectId, isControlled, controlledOptions])

    const handleSelect = (epicId) => {
        onChange?.(epicId)
        setVisible(false)
    }

    const panelContent = (
        <EpicPickerPanel
            active={visible}
            projectId={projectId}
            value={value}
            onChange={handleSelect}
            options={controlledOptions}
            placeholder={placeholder}
            disabled={disabled}
            enabled={enabled}
            showEpicKey={!compact}
        />
    )

    return (
        <Popover
            content={panelContent}
            trigger="click"
            placement="bottomLeft"
            open={disabled ? false : visible}
            onOpenChange={(open) => {
                if (!disabled) setVisible(open)
            }}
            arrow={false}
            styles={{
                root: { paddingTop: 4 },
                body: {
                    padding: 0,
                    background: 'transparent',
                    boxShadow: 'none',
                },
            }}
            getPopupContainer={() => document.body}
        >
            <button
                type="button"
                className={[
                    styles.trigger,
                    compact ? styles.triggerCompact : '',
                    className,
                ]
                    .filter(Boolean)
                    .join(' ')}
                disabled={disabled}
                aria-haspopup="listbox"
                aria-expanded={visible}
            >
                <span className={styles.triggerInner}>
                    {selectedItem ? (
                        <span className={styles.triggerValue}>
                            {!compact && selectedItem.epicKey ? (
                                <span className={styles.triggerKey}>
                                    {selectedItem.epicKey}
                                </span>
                            ) : null}
                            <span className={styles.triggerTitle}>
                                {selectedItem.label}
                            </span>
                        </span>
                    ) : (
                        <span className={styles.triggerPlaceholder}>
                            {placeholder}
                        </span>
                    )}
                    {!compact ? (
                        <Icon
                            icon={
                                visible ? 'mdi:chevron-up' : 'mdi:chevron-down'
                            }
                            width={20}
                            height={20}
                            className={styles.chevron}
                        />
                    ) : null}
                </span>
            </button>
        </Popover>
    )
}

export default EpicSelector
